/*
Copyright 2023, 2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import {
  AudioPresets,
  DefaultReconnectPolicy,
  type RoomOptions,
  ScreenSharePresets,
  type TrackPublishDefaults,
  VideoPreset,
  VideoPresets,
} from "livekit-client";

/**
 * Приоритет у звука (CTO 18.09.2026: «приоритет должен быть у звука»).
 *
 * Замер того же дня на машине CTO: с включённой камерой наружу уходило 1,7–2,9 Мбит/с,
 * без неё — 0,37; собеседница слышала его плохо ровно пока шло видео. Звук весит около
 * 50 кбит/с и стоит с видео в одной очереди на выход: при заторе опоздавший пакет речи
 * бесполезен. Поэтому:
 * - звуку — высокий приоритет кодирования и сети (браузер помечает его пакеты и при
 *   нехватке полосы режет видео, а не речь);
 * - камере — низкий приоритет и потолок 1 Мбит/с вместо 1,7;
 * - при заторе камера снижает и разрешение, а не только частоту кадров.
 */
export const CAMERA_MAX_BITRATE = 1_000_000;

const cameraEncoding = {
  maxBitrate: CAMERA_MAX_BITRATE,
  maxFramerate: 30,
  priority: "low" as RTCPriorityType,
};

const defaultLiveKitPublishOptions: TrackPublishDefaults = {
  audioPreset: { ...AudioPresets.music, priority: "high" },
  dtx: true,
  // disable red because the livekit server strips out red packets for clients
  // that don't support it (firefox) but of course that doesn't work with e2ee.
  red: false,
  forceStereo: false,
  simulcast: true,
  // Нижние слои камеры тоже уступают звуку.
  videoSimulcastLayers: [VideoPresets.h180, VideoPresets.h360].map(
    (preset) =>
      new VideoPreset(
        preset.width,
        preset.height,
        preset.encoding.maxBitrate,
        preset.encoding.maxFramerate,
        "low",
      ),
  ),
  screenShareEncoding: ScreenSharePresets.h1080fps30.encoding,
  // Демонстрация экрана начиналась размытой и становилась читаемой через пару минут:
  // рядом с чётким слоем публиковались низкие, приём начинался с них и полз вверх.
  // Для экрана низкие слои бесполезны — там важен текст, а не плавность.
  screenShareSimulcastLayers: [] as VideoPreset[],
  // Камера при нехватке полосы снижает и разрешение, и частоту кадров. Чёткость любой
  // ценой нужна только показу экрана — там её задаём отдельно (SCREEN_SHARE_PUBLISH_OPTIONS).
  degradationPreference: "balanced",
  stopMicTrackOnMute: false,
  videoCodec: "vp8",
  videoEncoding: cameraEncoding,
  backupCodec: { codec: "vp8", encoding: cameraEncoding },
} as const;

export const defaultLiveKitOptions: RoomOptions = {
  // automatically manage subscribed video quality
  adaptiveStream: true,

  // optimize publishing bandwidth and CPU for published tracks
  dynacast: true,

  // capture settings
  videoCaptureDefaults: {
    resolution: VideoPresets.h720.resolution,
  },

  // publish settings
  publishDefaults: defaultLiveKitPublishOptions,

  // default LiveKit options that seem to be sane
  stopLocalTrackOnUnpublish: true,
  reconnectPolicy: new DefaultReconnectPolicy(),
  disconnectOnPageLeave: true,
  webAudioMix: false,
};

/**
 * Показ экрана: текст и таблицы должны читаться, поэтому здесь при нехватке полосы
 * жертвуем частотой кадров, а не чёткостью (решение 10.08.2026). Приоритет ниже звука.
 */
export const SCREEN_SHARE_PUBLISH_OPTIONS = {
  degradationPreference: "maintain-resolution" as RTCDegradationPreference,
  screenShareEncoding: {
    ...ScreenSharePresets.h1080fps30.encoding,
    priority: "medium" as RTCPriorityType,
  },
};

/** Переключатель для замеров: `klava-force-red` = `on` | `off` в хранилище браузера. */
function redOverride(): boolean | null {
  try {
    const value = localStorage.getItem("klava-force-red");
    if (value === "on") return true;
    if (value === "off") return false;
  } catch {
    // Приватный режим: хранилище недоступно — идём с обычной настройкой.
  }
  return null;
}

/**
 * Избыточность звука (RED): в каждый пакет кладётся копия предыдущего, и потеря пакета
 * не рвёт речь.
 *
 * Раньше она выключалась в комнатах со сквозным шифрованием — а это все рабочие чаты.
 * Замер на стенде 24.09.2026 (потери 20%, задержка 80±30 мс): с избыточностью до уха
 * доходит рваными 2–3% звука, и видно 1100–1400 восстановленных пакетов. Увеличение
 * запаса приёмника от потерь не спасает — оно лечит дрожание.
 *
 * Оговорка авторов LiveKit: сервер вырезает избыточные пакеты для тех, кто их не
 * понимает (Firefox), и при сквозном шифровании разбор у такого участника ломается.
 * У нас люди сидят в Клаве на Chromium (десктоп и веб) и в мобильных сборках, поэтому
 * избыточность включена всем; переключатель оставлен для замеров и быстрого отката.
 */
export function publishDefaultsFor(
  mediaEncrypted: boolean,
): TrackPublishDefaults {
  void mediaEncrypted;
  return { ...defaultLiveKitPublishOptions, red: redOverride() ?? true };
}
